import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { systemSupportAPI, usersAPI, tasksAPI } from '../utils/api';
import { useToast, apiError } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import { format, formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Bug as BugIcon, Monitor, Server, Zap } from 'lucide-react';

/**
 * One distinct problem, with its occurrences.
 *
 * The technical half — stack traces, request context, user agents — is sent by
 * the API only to Developer and Admin levels. The `technical` flag in the
 * response says whether it was, so this page does not have to guess, and the
 * data never reaches a browser that should not have it.
 */

const SAPPHIRE = '#2E539F';
const DANGER   = '#ef4444';
const SEVERITY = {
  Critical: { fg: DANGER,    bg: 'rgba(239,68,68,.10)' },
  High:     { fg: '#8A6400', bg: 'rgba(255,200,73,.20)' },
  Medium:   { fg: '#2f8a84', bg: 'rgba(92,195,188,.16)' },
  Low:      { fg: SAPPHIRE,  bg: 'rgba(46,83,159,.10)' },
};
const STATUSES = ['New','Regressed','Investigating','Confirmed','In Progress','Resolved','Verified','Closed','Ignored','Duplicate'];
const KIND_ICON = { react_render: Monitor, window_error: Monitor, unhandled_rejection: Zap, api: Server, server: Server };
const KIND_LABEL = {
  react_render: 'Page crash', window_error: 'Browser error',
  unhandled_rejection: 'Unhandled promise', api: 'API failure', server: 'Server error',
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider mb-0.5"
      style={{ color: 'var(--text-secondary)' }}>{label}</p>
    <div className="text-sm" style={{ color: 'var(--text)' }}>{children ?? '—'}</div>
  </div>
);

export default function SystemSupportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [data, setData]   = useState(null);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [openOcc, setOpenOcc] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await systemSupportAPI.getById(id);
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e, 'Could not load this error.'));
    } finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    usersAPI.getAll().then(r => setUsers(r.data || [])).catch(() => setUsers([]));
    tasksAPI.getAll({ limit: 0 }).then(r => setTasks(r.data || [])).catch(() => setTasks([]));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await systemSupportAPI.update(id, patch);
      toast.success('Updated');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Could not update this error.'));
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!data) return null;

  const sev = SEVERITY[data.severity] || SEVERITY.Medium;
  const Icon = KIND_ICON[data.kind] || BugIcon;
  // The API decides. If it did not send technical fields, this user may not
  // see them — there is nothing to hide client-side because nothing arrived.
  const technical = !!data.technical;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate('/system-support')}
        className="flex items-center gap-1.5 text-sm" style={{ color: SAPPHIRE }}>
        <ArrowLeft size={15} /> System Support
      </button>

      {/* ── Overview ─────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: sev.bg, color: sev.fg }}>
            <Icon size={19} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold break-words" style={{ color: 'var(--text)' }}>
              {data.title}
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {KIND_LABEL[data.kind] || data.kind}
              {data.error_type ? ` · ${data.error_type}` : ''}
              {data.module ? ` · ${data.module}` : ''}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-4 border-t"
          style={{ borderColor: 'var(--border)' }}>
          <Field label="Occurrences">
            <span className="text-xl font-bold">{data.occurrence_count}</span>
          </Field>
          <Field label="People affected">
            <span className="text-xl font-bold">{data.affected_users}</span>
          </Field>
          <Field label="First seen">
            {data.first_seen ? format(new Date(data.first_seen), 'd MMM yyyy HH:mm') : '—'}
          </Field>
          <Field label="Last seen">
            {data.last_seen
              ? `${formatDistanceToNow(new Date(data.last_seen), { addSuffix: true })}`
              : '—'}
          </Field>
        </div>
      </div>

      {/* ── Managing it ──────────────────────────────────────────────────── */}
      {technical && (
        <div className="card">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: SAPPHIRE }}>
            Manage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="label">Status</label>
              <select className="select" value={data.status} disabled={saving}
                onChange={e => save({ status: e.target.value })}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Severity</label>
              <select className="select" value={data.severity} disabled={saving}
                onChange={e => save({ severity: e.target.value })}>
                {Object.keys(SEVERITY).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Assigned to</label>
              <select className="select" value={data.assigned_to || ''} disabled={saving}
                onChange={e => save({ assigned_to: e.target.value })}>
                <option value="">Unassigned</option>
                {users.filter(u => u.is_active).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Development task</label>
              <select className="select" value={data.task_id || ''} disabled={saving}
                onChange={e => save({ task_id: e.target.value })}>
                <option value="">Not linked</option>
                {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
              {data.task_id && (
                <Link to={`/tasks/${data.task_id}`} className="text-xs mt-1 inline-block"
                  style={{ color: SAPPHIRE }}>
                  Open {data.task_title || 'the task'}
                </Link>
              )}
            </div>
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--text-secondary)' }}>
            A TMS error links to a development <strong>task</strong>, not a bug —
            bugs belong to client products and are raised in Dev Hub.
          </p>
          {data.resolved_at && (
            <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
              Resolved {format(new Date(data.resolved_at), 'd MMM yyyy HH:mm')}
              {data.resolved_by_name ? ` by ${data.resolved_by_name}` : ''}.
              If it happens again it will reopen automatically.
            </p>
          )}
        </div>
      )}

      {/* ── Occurrences ──────────────────────────────────────────────────── */}
      <div className="card p-0">
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: SAPPHIRE }}>
            Occurrences
          </h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            The 50 most recent. Older ones are removed after 30 days; the counts above are kept.
          </p>
        </div>

        {data.occurrences?.length === 0 ? (
          <p className="px-4 py-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
            No occurrences retained. The counts above still reflect everything seen.
          </p>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {data.occurrences.map(o => (
              <div key={o.id}>
                <button
                  onClick={() => setOpenOcc(openOcc === o.id ? null : o.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/[.02]"
                  disabled={!technical}>
                  <span className="text-xs w-36 flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                    {o.occurred_at ? format(new Date(o.occurred_at), 'd MMM HH:mm:ss') : '—'}
                  </span>
                  <span className="text-sm flex-1 truncate" style={{ color: 'var(--text)' }}>
                    {o.route || o.api_endpoint || '—'}
                  </span>
                  {o.http_status && (
                    <span className="text-xs font-semibold" style={{ color: DANGER }}>{o.http_status}</span>
                  )}
                  <span className="text-xs w-32 truncate text-right" style={{ color: 'var(--text-secondary)' }}>
                    {o.user_name || 'signed out'}
                  </span>
                  {o.reference && (
                    <span className="text-xs font-bold tracking-wider w-16 text-right"
                      style={{ color: 'var(--text-secondary)', fontFamily: 'Consolas, monospace' }}>
                      {o.reference}
                    </span>
                  )}
                </button>

                {technical && openOcc === o.id && (
                  <div className="px-4 pb-4 space-y-3" style={{ background: 'var(--surface-alt)' }}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                      <Field label="Method">{o.http_method}</Field>
                      <Field label="Status">{o.http_status}</Field>
                      <Field label="Request id">
                        <span style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>{o.request_id}</span>
                      </Field>
                      <Field label="Browser">
                        <span className="text-xs break-all">{o.user_agent}</span>
                      </Field>
                    </div>
                    {o.stack && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider mb-1"
                          style={{ color: 'var(--text-secondary)' }}>Stack</p>
                        <pre className="text-xs p-3 rounded-lg overflow-x-auto"
                          style={{ background: 'var(--surface)', color: 'var(--text)',
                                   fontFamily: 'Consolas, monospace', maxHeight: 280 }}>
{o.stack}
                        </pre>
                      </div>
                    )}
                    {o.context && Object.keys(o.context).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider mb-1"
                          style={{ color: 'var(--text-secondary)' }}>Context</p>
                        <pre className="text-xs p-3 rounded-lg overflow-x-auto"
                          style={{ background: 'var(--surface)', color: 'var(--text)',
                                   fontFamily: 'Consolas, monospace', maxHeight: 200 }}>
{JSON.stringify(o.context, null, 2)}
                        </pre>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                          Passwords, tokens and request bodies are never recorded.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!technical && (
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          Technical detail — stack traces and request information — is available
          to developers and administrators.
        </p>
      )}
    </div>
  );
}
